import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

import { Constants } from './constants/manage-survey-tabs.constants';

import { AuthService } from '../shared/services/auth/auth.service';

import { AuthState } from '../shared/interfaces/auth.interface';
import { Survey } from '../shared/interfaces/survey.interface';
import { EventEmitterService } from '../shared/services/event-emitter/event-emitter.service';

@Component({
  selector: 'ps-ui-manage-survey-tabs',
  templateUrl: './manage-survey-tabs.component.html',
  styleUrls: ['./manage-survey-tabs.component.css'],
})
export class ManageSurveyTabsComponent implements OnChanges, OnInit, OnDestroy {
  public tabs: Tab[] = Constants.MANAGE_SURVEY_TABS;
  public activeTabIndex: number = 0;
  public activeTab: Tab = this.tabs[this.activeTabIndex];
  public enableTrafficChannels: boolean = false;
  private _userType!: string;
  @Input() survey: Survey = {} as Survey;
  @Input() set auth(data: AuthState) {
    this._auth.setState(data);
    this.enableTrafficChannels =
      !!this._auth?.buyerConfig?.enableTrafficChannels;
    this.isEnableHealthMetrics =
      this._auth.buyerConfig?.enableHealthMetrics ?? false;
  }
  @Input() isCpiEditModeDisabled: boolean = false;
  public isServiceBuyerLoggedIn: boolean = false;
  public isEnableHealthMetrics: boolean = false;
  public targetedActiveTab: string | null;
  private _subscription: Subscription = new Subscription();

  constructor(
    private _auth: AuthService,
    private _router: ActivatedRoute,
    private _eventEmitterService: EventEmitterService
  ) {
    this.targetedActiveTab = this._router.snapshot.paramMap.get('activeTab');
  }

  ngOnInit(): void {
    const authServiceSubscription =
      this._auth.loggedInAsServiceBuyer$.subscribe((isLoggedIn) => {
        if (isLoggedIn) {
          this.isServiceBuyerLoggedIn = true;
        }
      });
    this._subscription.add(authServiceSubscription);
  }

  ngOnChanges(changes: SimpleChanges = {}): void {
    this._userType = this._auth.userType;
    if (this._userType === 'supplier') {
      this.tabs = Constants.MANAGE_SURVEY_TABS.filter((tab) =>
        Constants.SURVEY_SUPPLIER_TAB_KEY.includes(tab.key)
      );
    } else {
      this.tabs = this._manageTab();
    }
    const activeIndex = this.tabs.findIndex(
      (tab) => tab.key === this.targetedActiveTab
    );
    const activeTabIndex = activeIndex !== -1 ? activeIndex : 0;
    if (!this._isChangeSurveyStatus(changes)) {
      this.setActiveTab(activeTabIndex);
    } else {
      this._emitChangeSurveyStatus(changes);
    }
  }

  private _manageTab() {
    const _tabsToRemove: string[] = [];
    if (this._enableMultiCountry()) {
      _tabsToRemove.push('MULTI_COUNTRY');
    }
    if (!this._enableTC()) {
      _tabsToRemove.push('TRAFFIC_CHANNELS');
    }
    if (!this._showReconTab()) {
      _tabsToRemove.push('RECONCILE');
    }
    if (!this._showMarginMaxTab()) {
      _tabsToRemove.push('MARGIN_MAX');
    }
    if (!this._showHealthChecksTab()) {
      _tabsToRemove.push('HEALTH_CHECKS');
    }
    return Constants.MANAGE_SURVEY_TABS.filter(
      (tab) => !_tabsToRemove.includes(tab.key)
    );
  }

  private _enableMultiCountry() {
    return (
      this._userType === 'buyer' &&
      !(this.survey.mc_unique_id && this._auth.buyerConfig?.enableMultiCountry)
    );
  }

  private _enableTC() {
    return (
      this.survey.incl_excl !== 1 &&
      !this.survey.channelType &&
      ((this.enableTrafficChannels && this._userType === 'buyer') ||
        this._userType === 'operator')
    );
  }

  private _showReconTab() {
    if (this._userType === 'buyer') {
      return this._auth.buyerConfig?.reconciliations;
    }
    if (this._userType === 'operator') {
      return true;
    }
    return false;
  }

  private _showMarginMaxTab() {
    if (this._userType === 'operator' && this.survey.is_enable_margin_max) {
      return true;
    }
    return false;
  }

  private _showHealthChecksTab() {
    return this._userType === 'operator' || this.isEnableHealthMetrics;
  }

  setActiveTab(activeTabIndex: number) {
    this.activeTabIndex = activeTabIndex;
    this.activeTab = this.tabs[activeTabIndex];
  }

  private _isChangeSurveyStatus(changes: SimpleChanges) {
    return (
      changes.survey?.currentValue?.status !==
      changes.survey?.previousValue?.status
    );
  }

  private _emitChangeSurveyStatus(changes: SimpleChanges) {
    this._eventEmitterService.changeSurveyStatus({
      surveyId: this.survey.id,
      status: changes.survey.currentValue.status,
      channelType: this.survey.channelType,
    });
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
  }
}
interface Tab {
  key: string;
  title: string;
  active: boolean;
  className?: string;
}
